import { useGetPageActions } from '@legacy/apis/pagesAPI/designSevenAPI';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { DialogProps, Menu, MenuItem } from '@mui/material';
import {
  Dialog,
  DialogTitle,
  IconButton,
} from '@legacy/components/mui-components';
import { MoreVert } from '@mui/icons-material';
import { cloneElement, ReactElement, ReactNode, useState } from 'react';

interface IMenuButton {
  urlForTest?: string;
}

interface IModalItem {
  buttonText: ReactNode;
  closeText: string;
  headingComponent: React.ReactNode;
  childComponent: ReactNode;
  headingText: ReactNode;
  size?: DialogProps['maxWidth'];
  'data-automation-id'?: string;
}

const ModalItem = ({
  size,
  closeText,
  headingComponent,
  headingText,
  childComponent,
  buttonText,
  ...props
}: IModalItem) => {
  const [open, setOpen] = useState(false);
  const handleClose = () => setOpen(false);
  const childWithClose = cloneElement(
    childComponent as ReactElement<{ handleClose: () => void }>,
    {
      handleClose,
    },
  );

  return (
    <>
      <MenuItem
        onClick={() => setOpen(true)}
        data-automation-id={props['data-automation-id']}
      >
        {buttonText}
      </MenuItem>
      <Dialog
        fullWidth
        maxWidth={size}
        onClose={() => setOpen(false)}
        open={open}
      >
        <DialogTitle
          closeText={closeText}
          headingComponent={headingComponent}
          closeModal={() => setOpen(false)}
        >
          {headingText}
        </DialogTitle>
        {childWithClose}
      </Dialog>
    </>
  );
};

export const MenuButton = ({ urlForTest }: IMenuButton) => {
  const { t } = useTranslation('header');
  const { pathname } = useLocation();
  const { pageAction } = useGetPageActions(urlForTest || pathname);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  if (pageAction.length > 0)
    return (
      <>
        <IconButton
          id="ScreenHeaderMoreActionButton"
          aria-controls="ScreenHeaderMoreActionMenu"
          aria-haspopup="true"
          aria-expanded={open ? 'true' : 'false'}
          title={t('ScreenHeaderActionMenu')}
          data-automation-id="ScreenHeaderMoreActionButton"
          onClick={handleClick}
          size="small"
        >
          <MoreVert fontSize="small" />
        </IconButton>
        <Menu
          id="ScreenHeaderMoreActionMenu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          slotProps={{
            list: { 'aria-labelledby': 'ScreenHeaderMoreActionButton' },
          }}
          disableScrollLock
        >
          {pageAction.map((action) => {
            if (action.type === 'modal') {
              const { size, headingText, closeText, childComponent, children } =
                action;
              return (
                <ModalItem
                  size={(size as DialogProps['maxWidth']) ?? 'lg'}
                  closeText={closeText ?? 'Close'}
                  headingComponent={undefined}
                  headingText={headingText}
                  childComponent={childComponent}
                  buttonText={children}
                  data-automation-id={action['data-automation-id']}
                />
              );
            }

            if (action.type === 'link') {
              return (
                <MenuItem
                  component="a"
                  href={action.url}
                  data-automation-id={action['data-automation-id']}
                >
                  {action.children}
                </MenuItem>
              );
            }

            return null;
          })}
        </Menu>
      </>
    );

  return null;
};
